import type { FunctionComponent, ReactNode } from "react";
import { Fragment, memo, Suspense, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { ArticlePaywallChecker } from "~/components/article/article-paywall-checker";
import { ArticlePaywallCheckerProvider } from "~/components/article/article-paywall-checker/contexts";
import { ArticleRender } from "~/components/article/article-render";
import { config } from "~/data";
import { useInfiniteScrollTriggerReference } from "~/lib/hooks";
import type { articleListArticle$key } from "~/queries/__generated__/articleListArticle.graphql";

import {
  useArticlePagination,
  useArticlePaginationChecker,
  useArticleRecommendation,
  useHandlePaginationNavigationHistory,
  useTrackPaginationCurrentArticle,
} from "./hooks";
import { Divider, Footer, Item, StyledLoading } from "./styles";

type Props = {
  footerSlot?: ReactNode;
  reference: articleListArticle$key;
};

export const ArticleList: FunctionComponent<Props> = ({ footerSlot, reference: reference_ }) => {
  const article = useFragment(
    graphql`
      fragment articleListArticle on Article
      @argumentDefinitions(customContents: { type: "[CustomContentInput]", defaultValue: [] }) {
        ...articleFooterArticle
        ...articlePaginationCheckerArticle
        ...articlePaginationNavigationHistoryArticle @arguments(customContents: $customContents)
        ...articleRecommendationArticle
        ...articlePaywallCheckerArticle
        ...articleRenderArticle @arguments(customContents: $customContents)
      }
    `,
    reference_,
  );

  const { articleRecommendation } = useArticleRecommendation(article);
  const { data, hasNext, isLoading, loadNext } = useArticlePagination(
    articleRecommendation?.results.infiniteScrollArticleIds ?? [],
  );
  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage: hasNext,
    isLoading,
    onLoadMore: loadNext,
  });

  const items = useMemo(() => {
    type Item = typeof article | (typeof data)[number];
    return [article, ...data] as [Item, ...Item[]];
  }, [article, data]);

  const { bindItemReference, currentArticle } = useTrackPaginationCurrentArticle(items);
  useHandlePaginationNavigationHistory(currentArticle);

  const { shouldStopPagination } = useArticlePaginationChecker(items);

  const shouldContinuePagination =
    config.features.enableInfiniteScroll && !shouldStopPagination && hasNext;

  return (
    <ArticlePaywallCheckerProvider>
      {items.map((item, index) => (
        <Fragment key={index}>
          <Item ref={bindItemReference(index)}>
            <MemorizedArticleRender reference={item} />
          </Item>
          <Divider />
          <Suspense>
            <ArticlePaywallChecker article={item} />
            {shouldContinuePagination && index === items.length - 1 && (
              <StyledLoading ref={infiniteScrollTriggerReference} />
            )}
            {!shouldContinuePagination && index === items.length - 1 && (
              <>
                <Footer
                  articleRecommendation={{
                    articleIds: articleRecommendation.results.moreOnThisTopicWidgetIds,
                    recommendationId: articleRecommendation.recommendationId,
                  }}
                  reference={article}
                />
                {footerSlot}
              </>
            )}
          </Suspense>
        </Fragment>
      ))}
    </ArticlePaywallCheckerProvider>
  );
};

const MemorizedArticleRender = memo(ArticleRender);

ArticleList.displayName = "ArticleList";
